/**
 * Pure logic behind the `credentials` form control — a credential RECORD whose inputs follow
 * `CREDENTIAL_FIELD_SPEC` (@inferrex/inference-schema) for the auth method held in a sibling
 * form field.
 *
 * Why a control and not a `json` field: a multi-value credential (an OAuth client id, secret and
 * token URL; an AWS key pair, region and service) pasted as one JSON blob is how DEBT-194 arose —
 * a typo in a key name stores a credential nothing can read, and the secret sits unmasked in a
 * textarea. Rendering one masked input per declared field, from the same table the connect form
 * and the server use, means the stored keys are always the ones sync reads.
 *
 * Secrets only travel one way. The control never receives a stored value (the list routes return
 * key NAMES only), so nothing here ever displays one back.
 */
import { credentialFieldsFor, type CredentialField } from '@inferrex/inference-schema';

/** The declared inputs for a method, or `[]` for an unknown / empty method (open vocabulary). */
export function credentialInputsFor(method: unknown): readonly CredentialField[] {
  return typeof method === 'string' && method !== '' ? credentialFieldsFor(method) : [];
}

/**
 * Keep only the method's declared fields, trimmed, dropping empties — what is actually sent.
 * A value typed under one method and left behind after switching to another is never submitted.
 */
export function cleanCredentialValues(method: unknown, values: unknown): Record<string, string> {
  const record = values && typeof values === 'object' && !Array.isArray(values) ? values as Record<string, unknown> : {};
  const out: Record<string, string> = {};
  for (const field of credentialInputsFor(method)) {
    const raw = record[field.name];
    if (typeof raw !== 'string') continue;
    const v = raw.trim();
    if (v !== '') out[field.name] = v;
  }
  return out;
}

/**
 * Why a credential record cannot be submitted, as user-facing messages, or `[]` when it can.
 * Names the missing fields by LABEL only — never echoes a value.
 */
export function credentialProblems(method: unknown, values: unknown): string[] {
  if (typeof method !== 'string' || method === '') return ['Choose how this system authenticates.'];
  const fields = credentialInputsFor(method);
  if (fields.length === 0) return [`"${method}" takes no credential to store.`];
  const clean = cleanCredentialValues(method, values);
  const missing = fields.filter((f) => f.required && !(f.name in clean)).map((f) => f.label);
  return missing.length > 0 ? [`Fill in: ${missing.join(', ')}.`] : [];
}
